import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { propIn } from '../../common/utils/immutableHelper';
import { Capacitor } from '@capacitor/core';
import { CAPACITOR_USER_INTERFACE_STYLE_PLUGIN } from '../../capacitor_plugins/pluginConstants';
import globalLogger from '../logger';
import { isPlatformIos } from '../platform/utils/platformDetailsUtils';
import platformSingleton from '../platform/platformSingleton';
import { UserInterfaceStylePlugin } from '../../capacitor_plugins/userInterfaceStyle';

const logger = globalLogger.createChannel('mobile.nativeThemeManager');

const selectAppTheme = propIn(['app', 'theme', 'theme']);
const selectAppThemeMode = propIn(['app', 'theme', 'mode']);

const milanoteToCapacitorTheme: Record<string, string> = {
    ['light-theme']: 'LIGHT',
    ['dark-theme']: 'DARK',
};

const setUserInterfaceStyle = async (theme: string, appThemeMode: string) => {
    if (!Capacitor.isPluginAvailable(CAPACITOR_USER_INTERFACE_STYLE_PLUGIN)) return;
    if (!isPlatformIos(platformSingleton)) return;

    try {
        await UserInterfaceStylePlugin.setStyle({ style: theme, mode: appThemeMode });
    } catch (error) {
        logger.error('Failed to set keyboard theme', error);
    }
};

/**
 * Android only in practice: the StatusBar plugin is excluded from the iOS build (see capacitor.config.ts),
 * where the status bar text colour follows the interface style set above.
 */
const setStatusBarTheme = async (theme: string) => {
    if (!Capacitor.isPluginAvailable('StatusBar')) return;
    const { StatusBar } = await import('@capacitor/status-bar'); // can't import this on web or it'll throw an error

    // Like above -- this one is of type StatusBar.Style
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    StatusBar.setStyle({ style: theme as any });
};

const useNativeThemeManager = () => {
    const theme = useSelector(selectAppTheme);
    const appThemeMode = useSelector(selectAppThemeMode);
    useEffect(() => {
        const capacitorTheme = milanoteToCapacitorTheme[theme];

        setUserInterfaceStyle(capacitorTheme, appThemeMode);
        setStatusBarTheme(capacitorTheme);
    }, [theme, appThemeMode]);
};

export default useNativeThemeManager;
